import { ApiError, post } from '@/api/client';
import { type SessionUser, useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { track } from '@/marketing/analytics';
import { commitGeneration } from '@/shell/context';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { KontoBox, failureCode, failureText, hint, tokenFromQuery } from './shared';

type State = 'verifying' | 'done' | 'invalid' | 'expired' | 'used' | 'failed';

/** The link from the signup mail: POST /account/verify once with the token in the query. */
export default function BekreftEpost() {
  const token = tokenFromQuery(useLocation().search);
  const nav = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  // a missing token is the invalid state, and no call is made
  const [state, setState] = useState<State>(token ? 'verifying' : 'invalid');
  const [detail, setDetail] = useState<string | null>(null);
  const started = useRef(false); // StrictMode runs the effect twice; the secret is consumed once

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    post<{ token?: string; user?: SessionUser }>('/account/verify', { token })
      .then((r) => {
        // a demo opened from a typed address has no password: confirming ended its other sessions and
        // signs this browser in, back to setting up the company (security review 2026-10-08)
        if (r?.token && r.user) {
          setSession(r.token, r.user);
          commitGeneration(r.user.id, null, null);
          nav('/konto/opprett-bedrift', { replace: true });
          return;
        }
        setState('done');
      })
      .catch((ex) => {
        const code = failureCode(ex);
        track('onboarding_failed', {
          stage: 'email_confirmation',
          error_code: code === 'invalid' || code === 'expired' || code === 'used' ? code : 'other',
        });
        if (code === 'invalid' || code === 'expired' || code === 'used') setState(code);
        else {
          setState('failed');
          setDetail(ex instanceof ApiError ? failureText(ex) : null);
        }
      });
  }, [token, nav, setSession]);

  const again = (
    <span style={hint}>{T('Logg inn og be om en ny bekreftelse fra siden der du oppretter bedrift.')}</span>
  );
  return (
    <KontoBox title={T('Bekreft e-postadresse')}>
      <div className="opt" style={{ display: 'grid', gap: 10 }}>
        {state === 'verifying' && <span style={hint}>{T('Bekrefter adressen …')}</span>}
        {state === 'done' && (
          <>
            <output className="al info">{T('E-postadressen er bekreftet.')}</output>
            <Link to="/login" style={hint}>
              {T('Logg inn')}
            </Link>
          </>
        )}
        {state === 'invalid' && (
          <div className="al dgr">
            {T('Lenken er ikke gyldig. Sjekk at hele adressen fra e-posten ble med.')}
          </div>
        )}
        {state === 'expired' && (
          <>
            <div className="al dgr">{T('Lenken har utløpt. Den gjelder i 24 timer.')}</div>
            {again}
          </>
        )}
        {state === 'used' && (
          <>
            <div className="al dgr">{T('Lenken er allerede brukt.')}</div>
            {again}
          </>
        )}
        {state === 'failed' && (
          <div className="al dgr">
            {detail ?? T('Fikk ikke svar fra tjeneren. Last siden på nytt for å prøve igjen.')}
          </div>
        )}
      </div>
    </KontoBox>
  );
}
